// src/components/resources/tools/generators/shared/exportImage.ts

import { clientLogger, logClientError } from '~/lib/logger-client'
import { sendResourceEvent } from '~/lib/gtag'

/**
 * Shared image-export helper for the generator tools.
 *
 * Every generator that captures a styled preview as a downloadable image
 * (SocialMedia, FakeChat, Review, DataTable, Chart, Browser, Email,
 * ScreenshotBeautifier) had its own near-identical `html2canvas →
 * canvas.toBlob → <a download>` block with subtly inconsistent error
 * handling and analytics. This module centralises that flow so:
 *
 *   1. the capture/encode/download path is written once,
 *   2. a `resource_complete` success event AND a `resource_complete`
 *      `outcome: 'error'` failure event are emitted consistently on every
 *      surface (previously some paths silently `console.error`-ed with no
 *      analytics), and
 *   3. blob-null, zero-size canvas, and thrown errors all route to the
 *      same failure handler so a download can never fail silently.
 */

export type ExportFormat = 'png' | 'jpg' | 'webp'

/**
 * Which DOM-rasterization engine to use.
 * - `modern-screenshot` (default): serializes the node to an SVG `<foreignObject>`
 *   and lets the browser's own engine rasterize it — higher fidelity for modern
 *   CSS, actively maintained. Made the default by #341 (see the fidelity case
 *   study in `docs/case-studies/export-engine-fidelity.md`).
 * - `html2canvas`: re-implements CSS painting on a canvas; the long-standing
 *   engine here, but unmaintained (v1.4.1) and synchronous on the main thread.
 *   Retained as the automatic fallback (see {@link renderToCanvas}) and selectable
 *   explicitly for a node whose effect stack the foreignObject path can't render.
 */
export type ExportEngine = 'html2canvas' | 'modern-screenshot'

/**
 * Resolve AFTER the browser has actually painted a frame.
 *
 * `requestAnimationFrame` runs just BEFORE a frame's paint, so its callback (and
 * any microtask an `await` chains off it) still executes before pixels hit the
 * screen — a double-rAF is NOT enough to guarantee a paint before subsequent
 * synchronous work. Chaining a `setTimeout(0)` MACROTASK inside the rAF defers
 * the resolve until after that frame has painted, so a loading state committed by
 * the caller is on screen before the (multi-second) synchronous rasterization runs.
 * No-ops to a resolved promise when rAF is unavailable (SSR / jsdom) so callers
 * never hang.
 */
function nextPaint(): Promise<void> {
  return typeof requestAnimationFrame === 'function'
    ? new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)))
    : Promise.resolve()
}

/**
 * Warm the heavy capture chunk(s) during browser idle so the FIRST export click
 * doesn't pay its cold dynamic-import + module-eval cost on the interaction's
 * critical path. Measured: the cold import dominates the first Download-click INP
 * (~3.7s SocialMedia / ~4.9s AdMockup at 20× CPU vs ~0.2–0.5s once warm) — the
 * yield-before-capture can't decouple it because the eval is synchronous. Call
 * this when a heavy-preview generator mounts.
 *
 * For the default (`modern-screenshot`) it warms the primary chunk first, then
 * chains a SECOND idle callback to warm the `html2canvas` **fallback** chunk too —
 * otherwise the fallback path (browsers where modern-screenshot throws, i.e.
 * exactly the case the fallback exists for) would pay a cold html2canvas import on
 * the export click. Both warms stay unforced.
 *
 * **Only warms on a REAL idle signal** (`requestIdleCallback`, unforced — no
 * `timeout` option). The import's module-eval is itself the multi-second long
 * task we're hiding, so warming it during an ACTIVE interaction would just move
 * the jank, not remove it. `requestIdleCallback` (Chromium, Firefox, Safari
 * 16.4+) fires at genuine idle and never preempts the user.
 *
 * Where it's unavailable (older Safari/iOS) there is no reliable idle signal, so
 * we **do not warm at all** rather than force it on a timer. A timer can't tell
 * "idle" from "mid-interaction": a bare `setTimeout` fired unconditionally, and
 * an input-debounced timer still missed continuous-input controls like range
 * sliders that emit only `input`/`pointermove` during a drag —
 * any heuristic without a true idle signal reintroduces the mid-interaction long
 * task it's meant to avoid. The cold first-export tail on those browsers is the
 * acceptable cost (tracked in #341).
 *
 * No-op on the server. Best-effort: the export path imports the engine again
 * regardless (the module cache makes that a no-op once warmed). On idle-signal
 * browsers this works on MOBILE too — the INP-affected audience — where there's
 * no hover to prefetch on.
 *
 * @param engine which engine's chunk to preload — defaults to the export
 *   default (`modern-screenshot`); pass `'html2canvas'` for a tool pinned to it.
 * @returns a cleanup that cancels a still-pending warm (call it on unmount).
 */
export function warmExportPipeline(engine: ExportEngine = 'modern-screenshot'): () => void {
  if (typeof window === 'undefined' || typeof requestIdleCallback !== 'function') {
    return () => undefined
  }
  // Two literal imports (not a dynamic `import(variable)`) so the bundler can
  // still statically split each engine into its own chunk.
  const warmHtml2canvas = (): void => void import('html2canvas').catch(() => undefined)
  const warmModernScreenshot = (): void => void import('modern-screenshot').catch(() => undefined)

  let fallbackId: number | undefined
  // Unforced — no `timeout`, so this fires only at a real idle moment.
  const primaryId = requestIdleCallback(() => {
    if (engine === 'html2canvas') {
      warmHtml2canvas()
      return
    }
    warmModernScreenshot()
    // Chain a second (still unforced) idle warm for the html2canvas fallback
    // chunk so the fallback path isn't a cold import on the export click.
    fallbackId = requestIdleCallback(warmHtml2canvas)
  })
  return () => {
    cancelIdleCallback(primaryId)
    if (fallbackId !== undefined) cancelIdleCallback(fallbackId)
  }
}

const MIME_BY_FORMAT: Record<ExportFormat, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  webp: 'image/webp',
}

export interface CaptureAndDownloadOptions {
  /** The DOM node to rasterise. */
  element: HTMLElement
  /** Output format. Defaults to `'png'`. */
  format?: ExportFormat
  /**
   * Download filename WITHOUT extension. The extension is derived from the
   * *actual* encoded `blob.type`, not the requested `format` — per the HTML
   * spec `canvas.toBlob` silently falls back to `image/png` on runtimes that
   * can't encode the requested MIME (e.g. WebP/JPEG on some older webviews),
   * so deriving from the blob keeps the file name honest.
   */
  filename: string
  /** Encoder quality 0..1 for the lossy JPG/WebP encoders. PNG ignores it. */
  quality?: number
  /**
   * html2canvas render scale (device-pixel multiplier). Defaults to `2`
   * (retina). Raise it when the on-screen preview is small relative to the
   * desired output — e.g. the App Store Screenshot tool captures a ~260px
   * preview but needs a multi-thousand-pixel store asset, so it passes a
   * larger scale so the source raster is at least the target size before any
   * `exactSize` resample.
   */
  scale?: number
  /**
   * When set, the captured canvas is resampled to EXACTLY these pixel
   * dimensions before encoding. Use for outputs with a hard spec the
   * responsive-preview capture can't hit on the nose (App Store Connect / Play
   * Console reject off-by-one sizes). Combine with a `scale` large enough that
   * the captured raster is ≥ the target, so the resample downscales (sharp)
   * rather than upscales (soft). Aspect ratio should already match the target,
   * so this corrects sub-pixel rounding, not the shape.
   */
  exactSize?: { width: number; height: number }
  /**
   * Override the rasterization engine. Defaults to `modern-screenshot` (with an
   * automatic html2canvas fallback). Pass `'html2canvas'` for nodes whose effect
   * stack the foreignObject path distorts (e.g. ScreenshotBeautifier's
   * transform/tilt/backdrop-blur root). A global `window.__EXPORT_ENGINE__` is
   * honored only by the A/B comparison harness.
   */
  engine?: ExportEngine
  /** Resource slug used for analytics (e.g. `'browser-mockup-generator'`). */
  trackingSlug: string
  /** Extra analytics params merged into both success and failure events. */
  extraParams?: Record<string, unknown>
  /** Engagement timer reading, attached to both success and failure events. */
  engagementMs?: number
  /** Optional toast/UI hooks so callers keep their own copy + styling. */
  onSuccess?: () => void
  onError?: (error: unknown) => void
  /**
   * When `true`, captures the *full* content even if it overflows a fixed-height,
   * `overflow:auto` ancestor inside `element` (e.g. the phone-framed chat
   * previews scroll their message list inside a `h-[760px]` frame). Either engine
   * rasterises an element at its rendered box, so anything scrolled out of an
   * inner scroller is cropped. With this flag we grow the capture height to the
   * full scroll height and, in the engine's clone hook (`onclone` for
   * html2canvas, `onCloneNode` for modern-screenshot), neutralise inner clipping
   * via the shared {@link liftClippedOverflow} — lift `max-height`/fixed heights
   * to `none`/`auto` and switch `overflow:auto|scroll|hidden` to `visible` on
   * `element` and its descendants — so the whole transcript renders into the
   * canvas. Defaults to `false` so existing single-shot exporters are unaffected.
   */
  expandScrollableContent?: boolean
}

function trackExportFailure(
  trackingSlug: string,
  extraParams: Record<string, unknown> | undefined,
  engagementMs: number | undefined,
): void {
  sendResourceEvent('complete', {
    resourceType: 'tool',
    resourceSlug: trackingSlug,
    engagementMs,
    params: { tool_type: 'generator', outcome: 'error', error_type: 'export_failed', ...extraParams },
  })
}

/**
 * Largest hidden vertical overflow (`scrollHeight − clientHeight`) across the
 * element's descendant scrollers. Used to grow the capture height enough to
 * include content scrolled out of a nested `overflow:auto` region (e.g. the
 * chat message list inside a fixed-height phone frame).
 */
function maxDescendantOverflow(element: HTMLElement): number {
  let extra = 0
  for (const node of Array.from(element.querySelectorAll<HTMLElement>('*'))) {
    const overflow = node.scrollHeight - node.clientHeight
    if (overflow > extra) extra = overflow
  }
  return extra
}

// Marker set on the capture root so html2canvas's (single-arg) `onclone` can
// find the clone in the cloned document.
const EXPAND_MARKER = 'data-export-expand-root'

/**
 * Resolve the engine: explicit option wins, then a global `__EXPORT_ENGINE__`
 * override (set only by the A/B comparison harness), else the default
 * `modern-screenshot`. modern-screenshot rasterizes via the browser's own engine
 * (SVG `<foreignObject>`), which is higher-fidelity than html2canvas's CSS
 * reimplementation — it fixed real export bugs here (clipped headlines/URLs,
 * dropped logos + input placeholders) and blocks the main thread ~40–60% less.
 * html2canvas remains as an automatic fallback (see {@link renderToCanvas}).
 */
function resolveEngine(explicit?: ExportEngine): ExportEngine {
  if (explicit) return explicit
  if (typeof window !== 'undefined') {
    const override = (window as unknown as { __EXPORT_ENGINE__?: unknown }).__EXPORT_ENGINE__
    if (override === 'modern-screenshot' || override === 'html2canvas') return override
  }
  return 'modern-screenshot'
}

/** Outcome of {@link renderToCanvas}: the canvas plus which engine produced it. */
interface RenderResult {
  canvas: HTMLCanvasElement
  /** Engine that actually produced the canvas (after any fallback). */
  engineUsed: ExportEngine
  /** True when `modern-screenshot` failed and html2canvas produced the output. */
  usedFallback: boolean
}

/**
 * Rasterize `element` with the chosen engine. When `modern-screenshot` is used
 * and it throws OR yields an empty canvas (e.g. a browser-specific foreignObject
 * quirk), fall back to html2canvas so an export never silently fails — the
 * canvas-painting engine is the cross-browser safety net. Reports `usedFallback`
 * so callers can surface the fallback rate as a production signal (it's the one
 * cross-browser failure mode the throw/empty-canvas guard *can* catch; silent
 * fidelity drift can't trip it, so observing how often the catchable path fires
 * is the closest in-prod proxy).
 */
async function renderToCanvas(
  element: HTMLElement,
  engine: ExportEngine,
  renderOptions: RenderOptions,
): Promise<RenderResult> {
  if (engine !== 'modern-screenshot') {
    return { canvas: await renderWithHtml2canvas(element, renderOptions), engineUsed: 'html2canvas', usedFallback: false }
  }
  try {
    const canvas = await renderWithModernScreenshot(element, renderOptions)
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('modern-screenshot produced an empty canvas')
    }
    return { canvas, engineUsed: 'modern-screenshot', usedFallback: false }
  } catch (error) {
    // Recoverable: the export still succeeds via the html2canvas fallback below,
    // so this is warn-severity, not an error (routing it through logClientError
    // would raise a false error-level alert on the expected fallback path).
    clientLogger.warn('modern-screenshot capture failed; falling back to html2canvas', {
      component: 'exportImage',
      cause: error instanceof Error ? error.message : String(error),
    })
    return { canvas: await renderWithHtml2canvas(element, renderOptions), engineUsed: 'html2canvas', usedFallback: true }
  }
}

/**
 * Lift inner clipping on a CLONED subtree so content scrolled out of a nested
 * `overflow:auto` region (e.g. a chat list inside a fixed-height phone frame)
 * paints in full. Shared by both engines' clone hooks.
 */
function liftClippedOverflow(root: HTMLElement, view: Window | null): void {
  const scrollers = Array.from(root.querySelectorAll<HTMLElement>('*')).filter(node => {
    const s = view?.getComputedStyle(node)
    if (!s) return false
    return /(auto|scroll|hidden)/.test(s.overflow + s.overflowY + s.overflowX)
  })
  for (const scroller of scrollers) {
    scroller.style.overflow = 'visible'
    scroller.style.overflowY = 'visible'
    scroller.style.overflowX = 'visible'
    scroller.style.height = 'auto'
    scroller.style.maxHeight = 'none'
    let ancestor = scroller.parentElement
    while (ancestor) {
      ancestor.style.height = 'auto'
      ancestor.style.maxHeight = 'none'
      if (ancestor === root) break
      ancestor = ancestor.parentElement
    }
  }
}

interface RenderOptions {
  scale: number
  expandScrollableContent: boolean
  /**
   * Opaque background the engine paints behind the capture. Defaults to
   * transparent (`null`/`undefined`). The mockup downloads want transparency
   * (so a JPEG matte can be applied downstream); the buyer-persona PDF wants an
   * opaque theme color baked in.
   */
  backgroundColor?: string
}

/** Full capture height including any nested scrolled-out overflow. */
function fullCaptureHeight(element: HTMLElement): number {
  return Math.max(element.scrollHeight, element.offsetHeight) + maxDescendantOverflow(element)
}

async function renderWithHtml2canvas(
  element: HTMLElement,
  { scale, expandScrollableContent, backgroundColor }: RenderOptions,
): Promise<HTMLCanvasElement> {
  const html2canvas = (await import('html2canvas')).default
  const fullHeight = expandScrollableContent ? fullCaptureHeight(element) : undefined
  return html2canvas(element, {
    backgroundColor: backgroundColor ?? null,
    scale,
    useCORS: true,
    allowTaint: true,
    logging: false,
    ...(expandScrollableContent && fullHeight
      ? {
          height: fullHeight,
          windowHeight: fullHeight,
          onclone: (clonedDoc: Document) => {
            const clonedRoot = clonedDoc.querySelector<HTMLElement>(`[${EXPAND_MARKER}="1"]`)
            if (clonedRoot) liftClippedOverflow(clonedRoot, clonedDoc.defaultView)
          },
        }
      : {}),
  })
}

async function renderWithModernScreenshot(
  element: HTMLElement,
  { scale, expandScrollableContent, backgroundColor }: RenderOptions,
): Promise<HTMLCanvasElement> {
  const { domToCanvas } = await import('modern-screenshot')
  const fullHeight = expandScrollableContent ? fullCaptureHeight(element) : undefined
  // If the capture root carries its OWN transform (e.g. ScreenshotBeautifier's
  // preview zoom `scale()`), modern-screenshot bakes it into the foreignObject
  // AND sizes the output to the transformed bounding box, which distorts the
  // result. html2canvas instead renders the element's LAYOUT box ignoring its
  // own transform — match that: render the unscaled `offset` box and neutralize
  // the transform in the clone. No-op for the (untransformed) other generators.
  const view = element.ownerDocument.defaultView
  // Real browsers return `'none'` when there's no transform; jsdom returns `''`.
  // Treat both as "no transform" so this only triggers for an actual transform.
  const rootTransform = view?.getComputedStyle(element).transform
  const hasOwnTransform = !!rootTransform && rootTransform !== 'none'

  const sizeOptions = hasOwnTransform
    ? { width: element.offsetWidth, height: fullHeight ?? element.offsetHeight }
    : fullHeight
      ? { height: fullHeight }
      : {}

  return domToCanvas(element, {
    scale,
    // Default transparent — parity with html2canvas `backgroundColor: null`; an
    // explicit `backgroundColor` (e.g. the buyer-persona PDF's theme color) is
    // baked in instead.
    backgroundColor: backgroundColor ?? undefined,
    ...sizeOptions,
    // MUST be `onCloneNode` (fires ONCE on the cloned ROOT), NOT `onCloneEachNode`
    // (fires per descendant). Stripping the transform here neutralizes only the
    // capture root's OWN transform (e.g. ScreenshotBeautifier's preview-zoom
    // `scale()`); a per-node hook would also strip a descendant's transform — e.g.
    // the SB tilt (`perspective() rotateX/Y`) on an inner element — flattening the
    // very effect we're capturing. Keep this on the root-only hook.
    onCloneNode: (cloned: Node) => {
      if (!(cloned instanceof HTMLElement)) return
      if (hasOwnTransform) {
        cloned.style.transform = 'none'
        cloned.style.transformOrigin = ''
      }
      if (expandScrollableContent) {
        liftClippedOverflow(cloned, cloned.ownerDocument?.defaultView ?? null)
      }
    },
  })
}

/** Options for {@link captureToCanvas} — the engine-rasterization half shared by
 * the blob/download path and the buyer-persona PDF export. */
export interface CaptureToCanvasOptions {
  /** The DOM node to rasterise. */
  element: HTMLElement
  /** Render scale (device-pixel multiplier). Defaults to `2`. */
  scale?: number
  /** Resample to EXACTLY these pixel dimensions (see {@link CaptureAndDownloadOptions.exactSize}). */
  exactSize?: { width: number; height: number }
  /** Override the rasterization engine (see {@link CaptureAndDownloadOptions.engine}). */
  engine?: ExportEngine
  /** Capture the full content past inner scrollers (see {@link CaptureAndDownloadOptions.expandScrollableContent}). */
  expandScrollableContent?: boolean
  /** Opaque background baked into the capture. Defaults to transparent. */
  backgroundColor?: string
}

/** Result of a successful {@link captureToCanvas}. */
export interface CaptureToCanvasResult {
  /** The rasterized canvas (after any `exactSize` resample). */
  canvas: HTMLCanvasElement
  /** Engine that actually produced the canvas (after any fallback). */
  engineUsed: ExportEngine
  /** True when `modern-screenshot` failed and html2canvas produced the output. */
  usedFallback: boolean
}

/**
 * Rasterize `element` to a `<canvas>` through the shared pipeline: a
 * yield-before-capture paint, engine selection + transform-root handling, the
 * automatic html2canvas fallback, and the optional `exactSize` resample. Returns
 * `null` on a soft failure (empty canvas); a hard engine throw propagates.
 *
 * This is the lowest shared capture layer — {@link captureToBlob} encodes its
 * result, and the buyer-persona PDF export slices its canvas across pages — so
 * every export surface rasterizes through the SAME engine (default
 * `modern-screenshot`, fallback html2canvas) rather than a per-surface
 * html2canvas call.
 */
export async function captureToCanvas(options: CaptureToCanvasOptions): Promise<CaptureToCanvasResult | null> {
  const { element, scale = 2, exactSize, expandScrollableContent = false, backgroundColor } = options
  const engine = resolveEngine(options.engine)

  // Tag the capture root so html2canvas's (single-arg) `onclone` can locate its
  // clone in the cloned document. Cleared again in `finally`.
  if (expandScrollableContent) element.setAttribute(EXPAND_MARKER, '1')

  try {
    // Yield a paint BEFORE the (synchronous, multi-second on heavy previews)
    // rasterization, so a loading state the caller set on click (e.g.
    // `isExporting` → disabled "Exporting…" button) COMMITS AND PAINTS first.
    // Without this the click's "next paint" is delayed until after the whole
    // capture, charging the entire main-thread block to the interaction — the
    // dominant generator INP cost. The rasterization still runs and takes the
    // same wall-clock; it's just no longer attributed to the Download click.
    await nextPaint()

    const renderOptions: RenderOptions = { scale, expandScrollableContent, backgroundColor }
    const { canvas: rendered, engineUsed, usedFallback } = await renderToCanvas(element, engine, renderOptions)
    let canvas = rendered

    if (canvas.width === 0 || canvas.height === 0) return null

    // Resample to the exact requested pixel dimensions (App Store / Play specs
    // reject off-by-one sizes). The capture aspect already matches the target,
    // so this only corrects the sub-pixel rounding the responsive-preview
    // capture introduces; with a high enough `scale` it's a sharp downscale.
    if (exactSize && (canvas.width !== exactSize.width || canvas.height !== exactSize.height)) {
      const exact = document.createElement('canvas')
      exact.width = exactSize.width
      exact.height = exactSize.height
      const exactCtx = exact.getContext('2d')
      if (exactCtx) {
        exactCtx.imageSmoothingQuality = 'high'
        exactCtx.drawImage(canvas, 0, 0, exactSize.width, exactSize.height)
        canvas = exact
      }
    }

    return { canvas, engineUsed, usedFallback }
  } finally {
    if (expandScrollableContent) element.removeAttribute(EXPAND_MARKER)
  }
}

/** Options for {@link captureToBlob} — the capture/encode half shared by the
 * download path and ScreenshotBeautifier's clipboard copy. */
export interface CaptureToBlobOptions {
  /** The DOM node to rasterise. */
  element: HTMLElement
  /** Output format. Defaults to `'png'`. */
  format?: ExportFormat
  /** Encoder quality 0..1 for the lossy JPG/WebP encoders. PNG ignores it. */
  quality?: number
  /** Render scale (device-pixel multiplier). Defaults to `2`. */
  scale?: number
  /** Resample to EXACTLY these pixel dimensions before encoding (see {@link CaptureAndDownloadOptions.exactSize}). */
  exactSize?: { width: number; height: number }
  /** Override the rasterization engine (see {@link CaptureAndDownloadOptions.engine}). */
  engine?: ExportEngine
  /** Capture the full content past inner scrollers (see {@link CaptureAndDownloadOptions.expandScrollableContent}). */
  expandScrollableContent?: boolean
}

/** Result of a successful {@link captureToBlob}. */
export interface CaptureToBlobResult {
  /** The encoded image blob. */
  blob: Blob
  /** Format ACTUALLY encoded — `toBlob` falls back to PNG when an encoder is missing. */
  format: ExportFormat
  /** Engine that actually produced the canvas (after any fallback). */
  engineUsed: ExportEngine
  /** True when `modern-screenshot` failed and html2canvas produced the output. */
  usedFallback: boolean
}

/**
 * Capture `element` to an encoded image {@link Blob}: rasterize via
 * {@link captureToCanvas} (shared engine + fallback + `exactSize`), then apply
 * the JPEG white matte and encode. Returns `null` on a soft failure (empty
 * canvas or a null blob); a hard engine throw propagates to the caller.
 *
 * Shared by {@link captureAndDownload} (download) and ScreenshotBeautifier's
 * clipboard copy, so both produce pixel-identical output from the same engine.
 *
 * JPG output gets a white matte painted behind the composition (JPEG has no alpha
 * channel) so transparent padding doesn't render as black.
 */
export async function captureToBlob(options: CaptureToBlobOptions): Promise<CaptureToBlobResult | null> {
  const { format = 'png', quality = format === 'png' ? 1 : 0.92 } = options
  const mimeType = MIME_BY_FORMAT[format]

  const result = await captureToCanvas({
    element: options.element,
    scale: options.scale,
    exactSize: options.exactSize,
    engine: options.engine,
    expandScrollableContent: options.expandScrollableContent,
  })
  if (!result) return null

  const { canvas, engineUsed, usedFallback } = result

  // JPEG has no alpha — paint a white matte UNDER the existing pixels so
  // transparent padding doesn't render as black.
  if (mimeType === 'image/jpeg') {
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.globalCompositeOperation = 'destination-over'
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.globalCompositeOperation = 'source-over'
    }
  }

  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, mimeType, quality))
  if (!blob) return null

  // Derive the extension + reported format from the ACTUAL encoded blob, not the
  // requested format — `toBlob` falls back to PNG when the requested encoder is
  // unavailable, so the file name and analytics both track what was really
  // produced.
  const actualFormat: ExportFormat =
    blob.type === 'image/webp' ? 'webp' : blob.type === 'image/jpeg' ? 'jpg' : 'png'

  return { blob, format: actualFormat, engineUsed, usedFallback }
}

/**
 * Captures `element` (via {@link captureToBlob}, default `modern-screenshot` with
 * an automatic html2canvas fallback) at 2× and triggers a browser download in the
 * requested format. Resolves once the capture finishes AND the download is
 * triggered — callers can `await` it and reset their loading state in a `finally`
 * without re-enabling the button mid-encode. Never throws — failures are reported
 * via `onError` + a `resource_complete` error event.
 *
 * The success event records `export_engine` + `export_used_fallback` so the
 * cross-browser fallback rate is observable in production (#360 review).
 */
export async function captureAndDownload(options: CaptureAndDownloadOptions): Promise<void> {
  const { element, filename, trackingSlug, extraParams, engagementMs, onSuccess, onError } = options

  try {
    const result = await captureToBlob({
      element,
      format: options.format,
      quality: options.quality,
      scale: options.scale,
      exactSize: options.exactSize,
      engine: options.engine,
      expandScrollableContent: options.expandScrollableContent,
    })

    if (!result) {
      trackExportFailure(trackingSlug, extraParams, engagementMs)
      onError?.(new Error('Capture produced no image'))
      return
    }

    const { blob, format: actualFormat, engineUsed, usedFallback } = result
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.${actualFormat}`
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    sendResourceEvent('complete', {
      resourceType: 'tool',
      resourceSlug: trackingSlug,
      engagementMs,
      params: {
        tool_type: 'generator',
        export_format: actualFormat,
        export_engine: engineUsed,
        export_used_fallback: usedFallback,
        outcome: 'success',
        ...extraParams,
      },
    })
    onSuccess?.()
  } catch (error) {
    logClientError(error, { component: 'exportImage', context: 'Image export failed' })
    trackExportFailure(trackingSlug, extraParams, engagementMs)
    onError?.(error)
  }
}
